import React, { createContext, useState } from 'react';
import { SortColumn } from 'react-data-grid';

type TableStateContextType = {
  sortColumns: SortColumn[];
  setSortColumns: (sortColumns: SortColumn[]) => void;
  frozenColumns: string[];
  setFrozenColumns: React.Dispatch<React.SetStateAction<string[]>>;
  hiddenColumns: string[];
  setHiddenColumns: React.Dispatch<React.SetStateAction<string[]>>;
  onColumnVisibilityChange: (column: string, isVisible: boolean) => void;
  onColumnFrozenChange: (column: string, isFrozen: boolean) => void;
};

const TableStateContext = createContext<TableStateContextType | 'NOT_SET'>(
  'NOT_SET'
);

export const TableStateProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [sortColumns, setSortColumns] = useState<SortColumn[]>([]);
  const [frozenColumns, setFrozenColumns] = useState<string[]>([]);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);

  const onColumnFrozenChange = (column: string, isFrozen: boolean) => {
    if (isFrozen) {
      setFrozenColumns((prev) => [...prev, column]);
    } else {
      setFrozenColumns((prev) => prev.filter((c) => c !== column));
    }
  };

  const onColumnVisibilityChange = (column: string, isVisible: boolean) => {
    if (isVisible) {
      setHiddenColumns((prev) => prev.filter((c) => c !== column));
    } else {
      setHiddenColumns((prev) => [...prev, column]);
    }
  };

  return (
    <TableStateContext.Provider
      value={{
        sortColumns,
        setSortColumns,
        frozenColumns,
        setFrozenColumns,
        hiddenColumns,
        setHiddenColumns,
        onColumnVisibilityChange,
        onColumnFrozenChange,
      }}
    >
      {children}
    </TableStateContext.Provider>
  );
};

export const useTableState = () => {
  const context = React.useContext(TableStateContext);

  if (context === 'NOT_SET') {
    throw new Error(
      'useTableState must be used within a TableStateProvider. Wrap a parent component in <TableStateProvider> to fix this error.'
    );
  }
  return context;
};
